import React, { FC } from 'react';
import styled, { css } from 'styled-components';
import { ExpanderPanel } from './ExpanderPanel';
import { ExpanderPanelGroup } from './ExpanderPanelGroup';
import { Spacer } from './Spacer';
import { ContactListFragment } from '../types/graphqlTypes';
import { Section } from './Section';
import { Typography } from './Typography';
import HtmlContent from './Html';
import { Divider } from './Divider';

export interface ContactListProps extends ContactListFragment {
  width: 'narrow' | 'wide';
}

interface StyledContactListProps {
  width: 'narrow' | 'wide';
}

const StyledAddresses = styled.div<StyledContactListProps>`
  ${({ theme: { space, media }, width }) => css`
    display: grid;
    gap: ${space.lg};
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    @media (min-width: ${media.xl}) {
      grid-template-columns: repeat(auto-fit, minmax(${width === 'narrow' ? '300px' : '200px'}, 1fr));
    }
  `};
`;

export const ContactList: FC<Omit<ContactListProps, '__typename'>> = ({ divisions, width, ...rest }) => {
  if (divisions?.length < 1) {
    return <></>;
  }

  return (
    <Section contained {...rest} narrow={width === 'narrow'}>
      <ExpanderPanelGroup as="ul">
        {divisions?.length >= 1 &&
          divisions.map(({ id, name, divisionAddresses, content }, index) => (
            <ExpanderPanel
              key={id}
              title={name}
              as="li"
              open={index === 0}
              headingVariant="titleMedium"
              headingComponent="h2"
            >
              <Spacer>
                {/* Division address */}

                {divisionAddresses.length >= 1 && (
                  <StyledAddresses width={width}>
                    {divisionAddresses.map(
                      ({ name, line1, line2, town, postcode, telephone, email, contactName }, index) => (
                        <Spacer spacing="half" key={index}>
                          {name && (
                            <>
                              <Typography component="h4" variant="titleSmall">
                                {name}
                              </Typography>
                            </>
                          )}
                          <address>
                            {line1 && (
                              <>
                                {line1} <br />
                              </>
                            )}
                            {line2 && (
                              <>
                                {line2} <br />
                              </>
                            )}
                            {town && (
                              <>
                                {town} <br />
                              </>
                            )}
                            {postcode && (
                              <>
                                {postcode} <br />
                              </>
                            )}
                          </address>
                          {contactName && (
                            <>
                              <Typography component="h5" variant="titleSmall">
                                {contactName}
                              </Typography>
                            </>
                          )}
                          {telephone || email ? (
                            <ul>
                              {telephone && (
                                <li>
                                  <a href={`tel:${telephone.replace(/\s+/g, '')}`}>{telephone}</a>
                                </li>
                              )}
                              {email && (
                                <li>
                                  <a href={`mailto:${email}`}>{email}</a>
                                </li>
                              )}
                            </ul>
                          ) : (
                            ''
                          )}
                        </Spacer>
                      )
                    )}
                  </StyledAddresses>
                )}
                {content && divisionAddresses.length >= 1 ? <Divider /> : ''}
                {content && (
                  <>
                    <HtmlContent>{content}</HtmlContent>
                  </>
                )}
              </Spacer>
            </ExpanderPanel>
          ))}
      </ExpanderPanelGroup>
    </Section>
  );
};
